import React from 'react';
import { logger } from '../../utility/logger';

const CHUNK_ERROR_RELOAD_GUARD_KEY = 'globalErrorBoundaryChunkReloaded';

// A deploy overwrites the previous build's hashed chunk files. A tab that's
// been open since before the deploy still holds a bundle referencing the old
// hashes, so a lazy import (React.lazy/dynamic import) 404s with a
// ChunkLoadError. A fresh load fetches the current index.html/main bundle,
// which reference chunks that actually exist - so a one-time reload recovers.
// Guarded via sessionStorage (like CacheBuster's version check) so a genuine
// outage doesn't reload-loop the tab forever.
const isChunkLoadError = (error) =>
    error && (error.name === 'ChunkLoadError' || /Loading chunk [\w.-]+ failed/i.test(error.message || ''));

class GlobalErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, isChunkError: false };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, isChunkError: isChunkLoadError(error) };
    }

    componentDidCatch(error, errorInfo) {
        logger(`Error caught by boundary: ${error.message}`, 'error');
        if (process.env.NODE_ENV === 'development') {
            console.error('Error details:', error);
            console.error('Error info:', errorInfo);
        }

        if (isChunkLoadError(error) && typeof window !== 'undefined' && window.sessionStorage) {
            const alreadyReloaded = window.sessionStorage.getItem(CHUNK_ERROR_RELOAD_GUARD_KEY);
            if (!alreadyReloaded) {
                window.sessionStorage.setItem(CHUNK_ERROR_RELOAD_GUARD_KEY, '1');
                window.location.reload();
            }
        }
    }

    render() {
        if (this.state.hasError) {
            if (this.state.isChunkError) {
                // Reload is already in flight (or was already attempted this
                // session); render nothing rather than the crashed subtree.
                return null;
            }
            return (
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                    <p>Something went wrong. Please refresh the page.</p>
                    <button type="button" onClick={() => window.location.reload()}>
                        Refresh
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}

export default GlobalErrorBoundary;